import React, { useState } from 'react';
import { Eye, Edit, Download, Upload, Trash2, User, Clock, FileText, Filter } from 'lucide-react';

export default function AuditTrail() {
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [dateRange, setDateRange] = useState('7');

  const auditLogs = [
    {
      id: 1,
      action: 'document_viewed',
      document: 'Environmental Clearance Certificate',
      user: 'John Manager',
      timestamp: '2024-12-06 10:30:15',
      ipAddress: '192.168.1.100',
      details: 'Viewed document in preview mode',
      category: 'Environmental Permits'
    },
    {
      id: 2,
      action: 'document_modified',
      document: 'Factory License',
      user: 'Sarah Admin',
      timestamp: '2024-12-06 09:15:42',
      ipAddress: '192.168.1.101',
      details: 'Updated expiry date and compliance status',
      category: 'Operational Licenses'
    },
    {
      id: 3,
      action: 'document_uploaded',
      document: 'Fire Safety Inspection Report',
      user: 'Mike Safety',
      timestamp: '2024-12-05 16:45:30',
      ipAddress: '192.168.1.102',
      details: 'New document uploaded with supporting evidence',
      category: 'Safety Certifications'
    },
    {
      id: 4,
      action: 'document_downloaded',
      document: 'Hazardous Waste Authorization',
      user: 'John Manager',
      timestamp: '2024-12-05 14:20:18',
      ipAddress: '192.168.1.100',
      details: 'Downloaded for regulatory submission',
      category: 'Environmental Permits'
    },
    {
      id: 5,
      action: 'document_submitted',
      document: 'ISO 45001 Certificate',
      user: 'Sarah Admin',
      timestamp: '2024-12-04 11:30:00',
      ipAddress: '192.168.1.101',
      details: 'Submitted to regulatory body for approval',
      category: 'Quality Standards'
    },
    {
      id: 6,
      action: 'document_deleted',
      document: 'Obsolete Environmental Report',
      user: 'Admin User',
      timestamp: '2024-12-03 13:15:22',
      ipAddress: '192.168.1.103',
      details: 'Archived document removed from active repository',
      category: 'Environmental Permits'
    }
  ];

  const getActionIcon = (action) => {
    switch (action) {
      case 'document_viewed':
        return <Eye className="text-blue-600" size={20} />;
      case 'document_modified':
        return <Edit className="text-orange-600" size={20} />;
      case 'document_uploaded':
        return <Upload className="text-green-600" size={20} />;
      case 'document_downloaded':
        return <Download className="text-purple-600" size={20} />;
      case 'document_submitted':
        return <FileText className="text-teal-600" size={20} />;
      case 'document_deleted':
        return <Trash2 className="text-red-600" size={20} />;
      default:
        return <FileText className="text-gray-600" size={20} />;
    }
  };

  const getActionLabel = (action) => {
    return action?.replace('document_', '')?.split('_')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'document_viewed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'document_modified':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'document_uploaded':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'document_downloaded':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'document_submitted':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'document_deleted':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredLogs = auditLogs?.filter(log => {
    const matchesType = filterType === 'all' || log?.action === filterType;
    const matchesUser = filterUser === 'all' || log?.user === filterUser;
    return matchesType && matchesUser;
  });

  const uniqueUsers = [...new Set(auditLogs?.map(log => log?.user))];

  const stats = {
    totalActions: auditLogs?.length,
    views: auditLogs?.filter(l => l?.action === 'document_viewed')?.length,
    modifications: auditLogs?.filter(l => l?.action === 'document_modified')?.length,
    uploads: auditLogs?.filter(l => l?.action === 'document_uploaded')?.length
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Actions</p>
          <p className="text-2xl font-bold text-gray-900">{stats?.totalActions}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700 mb-1">Document Views</p>
          <p className="text-2xl font-bold text-blue-900">{stats?.views}</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-sm text-orange-700 mb-1">Modifications</p>
          <p className="text-2xl font-bold text-orange-900">{stats?.modifications}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-700 mb-1">New Uploads</p>
          <p className="text-2xl font-bold text-green-900">{stats?.uploads}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Actions</option>
              <option value="document_viewed">Viewed</option>
              <option value="document_modified">Modified</option>
              <option value="document_uploaded">Uploaded</option>
              <option value="document_downloaded">Downloaded</option>
              <option value="document_submitted">Submitted</option>
              <option value="document_deleted">Deleted</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User
            </label>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Users</option>
              {uniqueUsers?.map((user, idx) => (
                <option key={idx} value={user}>{user}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Period
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">
            <Download size={16} />
            Export Audit Report
          </button>
        </div>

        {filteredLogs?.map((log) => (
          <div
            key={log?.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                {getActionIcon(log?.action)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded border ${getActionColor(log?.action)}`}>
                    {getActionLabel(log?.action)}
                  </span>
                  <span className="text-sm text-gray-600">{log?.category}</span>
                </div>
                <h4 className="font-medium text-gray-900 mb-1">{log?.document}</h4>
                <p className="text-sm text-gray-600 mb-3">{log?.details}</p>
                <div className="flex items-center gap-6 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <User size={14} />
                    <span>{log?.user}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>{log?.timestamp}</span>
                  </div>
                  <div>
                    <span>IP: {log?.ipAddress}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {filteredLogs?.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* Compliance Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Audit Trail for Regulatory Compliance</h4>
            <p className="text-sm text-blue-800">
              Complete records of all document access, modifications, and submissions maintained for regulatory compliance verification and audit preparation. 
              All activities are timestamped with user identification and IP tracking for security and accountability.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}